import { TextField as MuiTextField } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useElementTracking } from "../../../analytics/analytics";
import { validationError } from "../../../moment/validation-error";

import type { ElementTracking } from "../../../analytics/analytics";
import type { TextFieldProps as MuiTextFieldProps } from "@mui/material";
import type { ChangeEvent, FC, FocusEvent, ReactElement } from "react";
import type { FieldValues, Path } from "react-hook-form";

type TextFieldProps = Omit<MuiTextFieldProps, "variant"> &
  ElementTracking & {
    variant?: MuiTextFieldProps["variant"] | "editabletext";
  };

export const CeTextField: FC<TextFieldProps> = function CeTextField({
  variant,
  analyticsCategory,
  sx = [],
  ...props
}): ReactElement {
  const analyticsProps = useElementTracking(analyticsCategory, props.id, "text-input");
  return (
    <MuiTextField
      {...props}
      variant={variant === "editabletext" ? "standard" : variant}
      sx={[
        ...(variant === "editabletext"
          ? [
              {
                "& .MuiInputBase-root": {
                  fontSize: "inherit",
                  fontWeight: "inherit",
                },
                "& .MuiInputBase-root::before": {
                  borderBottom: "none",
                },
              },
            ]
          : []),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...analyticsProps}
    />
  );
};

export const CeTextInput = <TFieldValues extends FieldValues>({
  label,
  name,
  showValidationErrors = true,
  onChange: externalOnChange,
  onBlur: externalOnBlur,
  ...props
}: {
  label?: string;
  name: Path<TFieldValues>;
  showValidationErrors?: boolean;
} & TextFieldProps): React.JSX.Element => {
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller<TFieldValues>
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
          field.onBlur();
          externalOnBlur?.(event);
        };

        const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
          field.onChange(event);
          externalOnChange?.(event);
        };

        return (
          <CeTextField
            {...field}
            fullWidth
            id={name}
            label={label}
            helperText={validationError(fieldState, showValidationErrors)?.message}
            error={Boolean(validationError(fieldState, showValidationErrors))}
            value={field.value || ""}
            onBlur={externalOnBlur ? handleBlur : field.onBlur}
            onChange={externalOnChange ? handleChange : field.onChange}
            {...props}
          />
        );
      }}
    />
  );
};
